import { useState } from 'react';
import * as fmt from '../format';
import type { DayRow } from '../models';
import type { Store } from '../store';

type Column = keyof Pick<
  DayRow,
  'date' | 'dayNumber' | 'miles' | 'dailyTargetMiles' | 'totalActualMiles' | 'diffMiles'
  | 'pctComplete' | 'ranking'
>;

/// A real sortable table, like the Mac app's - the full daily table rather
/// than the phone's summary rows.
export function History({ store }: { store: Store }) {
  const [sort, setSort] = useState<{ column: Column | 'steps'; ascending: boolean }>({
    column: 'date',
    ascending: false,
  });

  if (store.days.length === 0) return <p className="muted">No days recorded.</p>;

  const rows = [...store.days].sort((a, b) => {
    const pick = (row: DayRow) =>
      sort.column === 'steps' ? (row.steps ?? 0)
        : sort.column === 'date' ? row.date
        : row[sort.column];
    const left = pick(a);
    const right = pick(b);
    const order = left < right ? -1 : left > right ? 1 : 0;
    return sort.ascending ? order : -order;
  });

  const header = (column: Column | 'steps', label: string) => (
    <th onClick={() => setSort((s) => ({ column, ascending: s.column === column ? !s.ascending : true }))}>
      {label}
      {sort.column === column ? (sort.ascending ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const best = store.days.reduce((a, b) => (a.miles >= b.miles ? a : b));

  return (
    <>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {header('date', 'Date')}
              {header('dayNumber', 'Day')}
              {header('steps', 'Steps')}
              {header('miles', 'Miles')}
              {header('dailyTargetMiles', 'Target')}
              {header('totalActualMiles', 'Total')}
              {header('diffMiles', 'vs target')}
              {header('pctComplete', 'Complete')}
              {header('ranking', 'Rank')}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.date}>
                <td>{fmt.shortDate(row.date)}</td>
                <td>{row.dayNumber}</td>
                <td>{row.steps === null ? '—' : fmt.steps(row.steps)}</td>
                <td>{fmt.miles(row.miles)}</td>
                <td className="muted">{fmt.miles(row.dailyTargetMiles)}</td>
                <td>{fmt.miles(row.totalActualMiles)}</td>
                <td className={row.diffMiles >= 0 ? 'good' : 'warn'}>
                  {fmt.signedMiles(row.diffMiles)}
                </td>
                <td className="muted">{fmt.percent(row.pctComplete)}</td>
                <td>#{row.ranking}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">
        {store.days.length} days · best {fmt.miles(best.miles)} on {fmt.shortDate(best.date)}
      </p>
    </>
  );
}
